import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../models/filters/widget-filter-data.model';

export type CompleteDateRange = {
  startDate: Date;
  endDate: Date;
  previousCycleStartDate: Date;
  previousCycleEndDate: Date;
};

/**
 * True when the filter carries every date the summary endpoints need: the selected range and the
 * previous cycle it is compared against.
 *
 * The shared filter can hold a selection published by another page (accounts only, or a cleared
 * date range). A missing date must not reach the API, where it deserialises to the default
 * DateTime and overflows SQL Server's date range.
 */
export function hasCompleteDateRange<T extends Partial<DateIntervalFilterData>>(
  filter: T | null | undefined
): filter is T & CompleteDateRange {
  return (
    !!filter &&
    isValidDate(filter.startDate) &&
    isValidDate(filter.endDate) &&
    isValidDate(filter.previousCycleStartDate) &&
    isValidDate(filter.previousCycleEndDate)
  );
}

/**
 * Copies of the shared filter's account and date parts, for a modal's own filter components to start from.
 * Without them the date filter falls back to its default interval and publishes it, overwriting the page's
 * selection. Empty objects are returned when the shared filter has no complete range, which keeps the
 * filter components' default behaviour.
 */
export function splitWidgetFilter(filter: WidgetFilterData): { accountFilter: AccountFilterData; dateFilter: DateIntervalFilterData } {
  if (!hasCompleteDateRange(filter)) {
    return { accountFilter: {} as AccountFilterData, dateFilter: {} as DateIntervalFilterData };
  }

  return {
    accountFilter: { accountIds: [...(filter.accountIds ?? [])] },
    dateFilter: {
      startDate: filter.startDate,
      endDate: filter.endDate,
      granularity: filter.granularity,
      previousCycleStartDate: filter.previousCycleStartDate,
      previousCycleEndDate: filter.previousCycleEndDate,
      interval: filter.interval,
      isCustomDate: filter.isCustomDate
    }
  };
}

/**
 * True when both filters select the same accounts and date range, so re-publishing one would only make
 * every listener reload the data it already shows.
 */
export function isSameWidgetFilter(a: Partial<WidgetFilterData>, b: Partial<WidgetFilterData>): boolean {
  const accountsA = [...(a.accountIds ?? [])].sort((x, y) => x - y);
  const accountsB = [...(b.accountIds ?? [])].sort((x, y) => x - y);

  return accountsA.join() === accountsB.join()
    && a.interval === b.interval
    && a.startDate?.getTime() === b.startDate?.getTime()
    && a.endDate?.getTime() === b.endDate?.getTime();
}

function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}